import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '../../ThemeContext';

/**
 * 🎨 ThemedTooltip - Reusable Themed Tooltip Component
 * 
 * A consistent tooltip component that adapts to the app's theme
 * with proper styling for both light and dark modes.
 * 
 * @param {Object} props
 * @param {string|React.Node} props.title - Tooltip content (string or JSX)
 * @param {React.Node} props.children - Element to attach tooltip to
 * @param {string} props.placement - Tooltip placement (default: "top")
 * @param {boolean} props.arrow - Show arrow (default: true)
 * @param {Object} props.sx - Additional styling
 * @param {string} props.fontSize - Font size (default: "14px")
 * @param {Object} props.typographyProps - Props for Typography wrapper (when title is string)
 * @param {...Object} otherProps - Other Tooltip props
 */
const ThemedTooltip = ({
  title,
  children,
  placement = "top",
  arrow = true,
  sx = {},
  fontSize = "14px",
  typographyProps = {},
  ...otherProps
}) => {
  const { themeMode } = useTheme();

  // Determine if title needs Typography wrapper
  const tooltipTitle = typeof title === 'string' ? (
    <Typography variant="body2" sx={{ p: 0.5, ...typographyProps.sx }} {...typographyProps}>
      {title}
    </Typography>
  ) : title;

  // Theme-aware styling
  const tooltipStyles = {
    backgroundColor: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
    color: `${themeMode === 'dark' ? '#ffffff' : '#000000'} !important`,
    fontSize: fontSize,
    '& .MuiTooltip-arrow': {
      color: `${themeMode === 'dark' ? '#3f3c57' : '#e2e2e2'} !important`,
    },
    ...sx
  };

  return (
    <Tooltip
      title={tooltipTitle}
      arrow={arrow}
      placement={placement}
      componentsProps={{
        tooltip: {
          sx: tooltipStyles,
        },
      }}
      {...otherProps}
    >
      {children}
    </Tooltip>
  );
};

export default ThemedTooltip; 